import { useCallback, useEffect, useRef, useState } from 'react';
import { categoryLabels } from '../../utils/clientDocumentIntake';
import { extractKycDocuments } from '../kycDocumentIntake';
import { kycDocumentTargets, matchDocumentTarget, documentTargetForm } from '../kycDocumentTargets';
import { intakeAttachmentContext, staffIntakeRequest } from '../kycIntakeAttachment';

export default function useKycDocumentIntake({clientId, form, onSaveTarget, onEnsureRecord}) {
  const [session,setSession] = useState(null);
  const latest = useRef({}); latest.current = { form, onSaveTarget, onEnsureRecord, session };
  const lock = useRef(false); const generation = useRef(0); const controller = useRef(null);
  // Retain uploaded/saved state through retries so applying fields never creates
  // another copy of the original after a partially successful save.
  const uploads = useRef(new Map());
  useEffect(()=>{
    generation.current++; lock.current=false; setSession(null); uploads.current.clear();
    return ()=>{generation.current++;controller.current?.abort();};
  },[clientId]);
  const close = useCallback(async ()=>{
    if (lock.current) return;
    setSession(null);
    for (const upload of uploads.current.values()) {
      if (!upload.saved && !upload.saveAttempted) await staffIntakeRequest('cancel', { clientId, objectPath: upload.file.objectPath }).catch(()=>{});
    }
    uploads.current.clear();
  },[clientId]);
  const cancelProcessing = useCallback(() => {
    if (!latest.current.session?.busy) return;
    // Invalidate progress/completion callbacks before aborting, including
    // extraction work that finishes just as the cancellation is requested.
    generation.current++;
    controller.current?.abort();
    lock.current = false;
    latest.current.session = null;
    setSession(null);
  }, []);
  const start = useCallback(async (files, dropContext = null)=>{
    if (lock.current || latest.current.session) return;
    lock.current=true;
    const run = generation.current;
    controller.current = new AbortController();
    setSession({busy:true,message:'Reading document...',documents:[],queue:[],dropContext});
    try {
      const {documents, unidentified, failed} = await extractKycDocuments(files,latest.current.form,controller.current.signal,progress=>{
        if (run === generation.current) setSession(prev=>({...prev,message:progress.stage === 'extracting' && categoryLabels[progress.category]
          ? `Extracting data from ${categoryLabels[progress.category]}...` : 'Reading document...'}));
      });
      if (run !== generation.current) return;
      const targets = kycDocumentTargets(latest.current.form);
      const queue = documents.map(document => ({ ...document, targetId: matchDocumentTarget(document,targets) }));
      setSession({ queue, documents: queue.slice(0,1), busy:false, dropContext,
        notice: unidentified ? 'A document was not recognized reliably. It will not change any person’s details. Please use a clear document for a single holder.' : '',
        error: failed ? 'Some documents could not be read. Please upload those documents again.' : '' });
    } catch(e) {if(run===generation.current) setSession({documents:[],queue:[],error:e.message,busy:false});}
    finally {if(run===generation.current) lock.current=false;}
  },[]);
  async function finish(values, documents) {
    if (lock.current) return;
    const active = latest.current.session?.documents[0];
    const target = kycDocumentTargets(latest.current.form).find(t=>t.id === active?.targetId);
    if (!active || !target) return;
    lock.current=true; const run=generation.current;
    setSession(prev=>({...prev,saving:true,error:''}));
    try {
      await latest.current.onEnsureRecord?.();
      if (documents.length) {
        let upload = uploads.current.get(active.id);
        if (!upload) {
          const context = intakeAttachmentContext(active,target,latest.current.session.dropContext);
          const result = await staffIntakeRequest('start', { clientId, ...context, file: { ...active.file, sizeBytes: active.file.size } });
          upload = { file: result.file, context, saved:false };
          uploads.current.set(active.id,upload);
        }
        if (!upload.saved) {
          upload.saveAttempted = true;
          const result = await staffIntakeRequest('save', { clientId, objectPath: upload.file.objectPath, ...upload.context });
          upload.file = result.file; upload.saved = true;
        }
      }
      await latest.current.onSaveTarget(target.id,values);
      if(run===generation.current) {
        uploads.current.delete(active.id);
        setSession(prev=>{
          const queue = prev.queue.slice(1);
          return queue.length ? { ...prev, queue, documents: queue.slice(0,1), saving:false, choosingTarget:false } : null;
        });
      }
    } catch(e) {if(run===generation.current) setSession(prev=>({...prev,saving:false,error:e.message}));}
    finally {if(run===generation.current) lock.current=false;}
  }
  const active = session?.documents[0];
  const targets = kycDocumentTargets(form);
  const target = targets.find(t=>t.id === active?.targetId);
  return {session, pending:[], start, close, cancelProcessing, finish, savePending:async()=>{}, targets, target,
    reviewForm: target ? documentTargetForm(form,target) : form,
    chooseTarget: id => { if(lock.current || uploads.current.has(active?.id)) return; setSession(prev=>{
      const queue = prev.queue.map((document,index)=>index===0 ? {...document,targetId:id} : document);
      return {...prev,queue,documents:queue.slice(0,1),choosingTarget:false};
    }); },
    changeTarget:()=>{if(!lock.current && !uploads.current.has(active?.id)) setSession(prev=>({...prev,choosingTarget:true}));},
    targetLocked: uploads.current.has(active?.id),
    dismissNotice:()=>setSession(prev=>prev?.documents.length ? {...prev,notice:''} : null),
    remove:id=>{if(lock.current || uploads.current.has(id)) return; setSession(prev=>({...prev,documents:prev.documents.map(d=>d.id===id ? {...d,attachmentRemoved:true} : d)}));}
  };
}
